import { useEffect, useMemo, useRef } from "react";
import { useBrand } from "../hooks/useBrand";
import { useIpCapture } from "../hooks/useIpCapture";
import { BRAND_IDS } from "../config/brands";
import { DATASTORE_WEB_FORM_ID } from "../config/env";
import { normaliseDob, resolveMobileDetails, submitMotJourney } from "../utils/submission";
import {
 JOURNEY_VARIANTS,
 DUAL_REP_PARAMS_SESSION_KEY,
 buildSmsSubmissionFingerprint,
} from "../utils/journeyVariant";
import { saveSession } from "../utils/datastoreSession";
import { identifyKlaviyo, trackKlaviyo, subscribeKlaviyo } from "../utils/klaviyo";
import { trackCustomEvent, trackPageView } from "../utils/analytics";
import ErrorScreenContent from "./ErrorScreen";
import "./SmsLander.css";

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

const text = (value) => `${value || ""}`.trim();
const list = (value) =>
 text(value)
  .split(",")
  .map((item) => item.trim())
  .filter(Boolean);
const titleCase = (value) =>
 text(value)
  .toLowerCase()
  .replace(/(^|[\s'-])\p{L}/gu, (match) => match.toUpperCase());

/**
 * Parse the SMS lander query parameters into a structured customer object.
 */
export const parseSmsLanderParams = (params) => {
 const addressDisplay = [
  "address_line1",
  "address_line2",
  "address_line3",
  "address_line4",
  "address_city",
  "address_state",
  "address_postcode",
  "address_country",
 ]
  .map((key) => text(params.get(key)))
  .filter(Boolean)
  .join(", ");

 const line1 = text(params.get("address_line1"));
 const line2 = [
  text(params.get("address_line2")),
  text(params.get("address_line3")),
  text(params.get("address_line4")),
 ]
  .filter(Boolean)
  .join(", ");
 const city = text(params.get("address_city"));
 const postcode = text(params.get("address_postcode"));
 const currentAddress =
  line1 || line2 || city || postcode
   ? { line1: line1 || undefined, line2: line2 || undefined, city: city || undefined, postcode: postcode || undefined }
   : undefined;

 return {
  uniqueID: text(params.get("uniqueID")),
  title: text(params.get("title")),
  lenderEmail: text(params.get("to")),
  lenderName: text(params.get("lender_name")) || "your lender",
  caseIds: list(params.get("case_ids")),
  firstName: titleCase(params.get("first_name")),
  lastName: titleCase(params.get("last_name")),
  clientId: text(params.get("client_id")),
  agreementNumbers: list(params.get("agreement_numbers")),
  dateOfBirth: text(params.get("dob")),
  email: text(params.get("email")),
  mobileNumber: text(params.get("mobileNumber")) || text(params.get("mobile")),
  address: addressDisplay,
  currentAddress,
 };
};

const referenceFor = (data) =>
 [data.clientId, ...data.caseIds].filter(Boolean).join(", ");

// Shared body copy — kept in sync with the Klaviyo/email template's mailto.
const bodyLines = (data) => [
 "Dear Sir/Madam,",
 "",
 "I confirm that I have appointed My Law Matters, SRA number 800557, to act on my behalf in relation to any and all of my motor finance commission complaints.",
 "",
 "I confirm that I have withdrawn my authority and representation from any previous professional representative in relation to these complaints and that they are no longer authorised to act on my behalf.",
 "",
 "I also confirm that I do not authorise or require any compensation, redress or other payment arising from my complaints to be paid to any previous professional representative.",
 "",
 "Please remove any previous professional representative from your records, record My Law Matters as my sole appointed representative, and ensure that all future correspondence regarding my complaints is directed to My Law Matters.",
 "",
 "Kind regards,",
 `${data.firstName} ${data.lastName}`.trim(),
 `Reference: ${referenceFor(data)}`,
 `Agreement Number(s) - last 4 digits: ${data.agreementNumbers
  .filter(Boolean)
  .map((n) => n.slice(-4))
  .join(", ")}`,
];

const subjectFor = (data) =>
 `Motor Finance Commission Complaint - Ref: ${data.caseIds.join(", ")}`;

export const buildEmail = (data) => {
 const body = [
  ...bodyLines(data),
  "Date of Birth:",
  "Address:",
 ];

 return {
  subject: subjectFor(data),
  body: body.join("\r\n"),
 };
};

const buildMailtoBody = (data) => {
 const body = [...bodyLines(data)];
 if (data.dateOfBirth) {
  body.push(`Date of Birth: ${data.dateOfBirth}`);
 }
 if (data.address) {
  body.push(`Address: ${data.address}`);
 }
 return {
  subject: subjectFor(data),
  body: body.join("\r\n"),
 };
};

export const buildMailtoUrl = (data) => {
 if (!data.lenderEmail) return "#";
 const { subject, body } = buildMailtoBody(data);
 const query = new URLSearchParams({
  cc: "claims@mylawmatters.co.uk",
  subject,
  body,
 });
 return `mailto:${data.lenderEmail}?${query.toString().replace(/\+/g, "%20")}`;
};

const brandDetails = (brand) => ({
 supportingLogo: brand.id !== BRAND_IDS.MLM ? "/logos/MLM Logo without tagline.svg" : null,
 representativeName:
  brand.id === BRAND_IDS.CLAIM1
   ? "My Law Matters"
   : brand.id === BRAND_IDS.YCM
    ? "Your Claim Matters (YCM)"
    : "My Law Matters (MLM)",
 privacyUrl:
  brand.id === BRAND_IDS.CLAIM1
   ? "https://claims1.co.uk/privacy-policy/"
   : brand.id === BRAND_IDS.YCM
    ? "https://yourclaimmatters.co.uk/privacy-policy/"
    : "https://mylawmatters.co.uk/privacy-policy/",
 ccEmail: "claims@mylawmatters.co.uk",
});

// Brand-specific icons used across the Next Steps / action layout. Each brand
// ships its own themed icon set (from the reference email template), so colours
// can vary per brand.
const brandIcons = (brandId) => {
 if (brandId === BRAND_IDS.MLM) {
  return {
   nextSteps:
    "https://d3k81ch9hvuctc.cloudfront.net/company/WxDQUE/images/a5fb8df4-28d3-416d-af7c-95e35584f2da.png",
   action:
    "https://d3k81ch9hvuctc.cloudfront.net/company/WUe9ke/images/5243863a-5fa9-45e9-a760-564879095388.png",
   person:
    "https://d3k81ch9hvuctc.cloudfront.net/company/WxDQUE/images/fe186740-34ac-4a44-a26b-0322ae64ca3c.png",
  };
 }
 return {
  nextSteps:
   "https://d3k81ch9hvuctc.cloudfront.net/company/WUe9ke/images/b7763f6b-a5e2-47d6-a28b-e00ac2ee6c1d.png",
  action:
   "https://d3k81ch9hvuctc.cloudfront.net/company/WUe9ke/images/5243863a-5fa9-45e9-a760-564879095388.png",
  person:
   "https://d3k81ch9hvuctc.cloudfront.net/company/XH2x2K/images/5a576903-0f21-416f-8a50-920d095c2413.png",
 };
};

// ---------------------------------------------------------------------------
// Error page
// ---------------------------------------------------------------------------
function ErrorPage({ brand, message }) {
 return (
  <main className="sms-lander sms-lander--centered">
   <section className="sms-error" role="alert">
    <ErrorScreenContent brand={brand} message={message} />
   </section>
  </main>
 );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function SmsLander() {
 const brand = useBrand();
 const details = brandDetails(brand);
 const icons = brandIcons(brand.id);

 // --- Parse params from current URL ---
 const params = useMemo(
  () => new URLSearchParams(window.location.search),
  [],
 );

 const storedParams = useMemo(() => {
  const p = parseSmsLanderParams(params);

  if (window.location.search) {
   try {
    sessionStorage.setItem(
     DUAL_REP_PARAMS_SESSION_KEY,
     JSON.stringify(p),
    );
   } catch (_) {
    return p;
   }
  } else {
   try {
    const stored = sessionStorage.getItem(DUAL_REP_PARAMS_SESSION_KEY);
    if (stored) {
     return JSON.parse(stored);
    }
   } catch (_) {
    // malformed JSON → empty object → error page renders
   }
   return {};
  }

  return p;
 }, [params]);

 const customer = storedParams;
 const mailto = useMemo(() => buildMailtoUrl(customer), [customer]);

 const reference = useMemo(
  () =>
   customer.clientId || customer.firstName
    ? referenceFor(customer)
    : "",
  [customer],
 );

 const agreementShort = useMemo(
  () =>
   customer.agreementNumbers.length
    ? customer.agreementNumbers.map((n) => n.slice(-4)).join(", ")
    : "",
  [customer.agreementNumbers],
 );



 // --- Document title and tracking ---
 useEffect(() => {
  document.title = `${brand.displayName} - Contact your lender`;
  trackPageView("SmsLander", {
   caseCount: customer.caseIds?.length ?? 0,
   brand: brand.id,
  });
 }, [brand.displayName, brand.id, customer.caseIds?.length]);

 // --- Scrub URL after parsing and storing ---
 useEffect(() => {
  if (window.location.search) {
   try {
    window.history.replaceState({}, document.title, window.location.pathname);
   } catch (_) {
    // retain query string when replaceState is unavailable
   }
  }
 }, []);

 // --- Validity checks ---
 const hasLenderEmail = Boolean(customer.lenderEmail);
 const hasNames =
  Boolean(customer.firstName) && Boolean(customer.lastName);
 const hasIdentifier = Boolean(customer.uniqueID || customer.clientId);
 const isValid = hasLenderEmail && hasNames && hasIdentifier;

 const effectiveUniqueID = customer.uniqueID || customer.clientId;

 // --- IP capture (unconditionally called for hook ordering) ---
 const ipData = useIpCapture(isValid ? effectiveUniqueID : undefined);

 // --- Klaviyo — identify profile + track page view (once IP and email resolve) ---
 useEffect(() => {
  if (!ipData) return;
  const email = customer.email;
  if (!email) return;

  if (typeof window.klaviyo !== "undefined") {
   window.klaviyo.push([
    "identify",
    {
     email,
     phone_number: customer.mobileNumber || undefined,
     first_name: customer.firstName || undefined,
     last_name: customer.lastName || undefined,
    },
   ]);
  }

  identifyKlaviyo(
   email,
   customer.mobileNumber,
   ipData.ip,
   effectiveUniqueID,
   {
    firstName: customer.firstName,
    lastName: customer.lastName,
   },
  );

  trackKlaviyo("Viewed Page", {
   url: window.location.href,
   page_name: "SMS Lander",
   email,
  });
 }, [ipData, customer.email, customer.mobileNumber, customer.firstName, customer.lastName, effectiveUniqueID]);

 // --- Fire-and-forget Kount/datastore submission on button click ---
 const submittedRef = useRef(false);

 const handleSubmit = async () => {
  if (submittedRef.current) return;
  submittedRef.current = true;

  trackKlaviyo("Partial Submitted", {
   url: window.location.href,
   page_name: "SMS Lander",
   email: customer.email,
  });

  const customerForFingerprint = {
   firstName: customer.firstName,
   lastName: customer.lastName,
   caseIds: customer.caseIds,
   clientId: customer.clientId,
   agreementNumbers: customer.agreementNumbers,
   dateOfBirth: customer.dateOfBirth,
   lenderEmail: customer.lenderEmail,
  };

  try {
   const fingerprint = await buildSmsSubmissionFingerprint(
    customerForFingerprint,
   );

   // Skip if remote fingerprint matches (idempotent resubmit)
   if (
    fingerprint &&
    ipData?.journeySubmissionFingerprint &&
    fingerprint === ipData.journeySubmissionFingerprint
   ) {
    return;
   }

   const kountPrefill = {
    forename: customer.firstName,
    surname: customer.lastName,
    dateOfBirth: normaliseDob(customer.dateOfBirth),
   };
   if (customer.title) kountPrefill.title = customer.title;
   if (customer.email) kountPrefill.email = customer.email;
   const { mobileNumber: resolvedMobile } = resolveMobileDetails(customer.mobileNumber);
   kountPrefill.mobileNumber = resolvedMobile;
   if (customer.currentAddress) {
    kountPrefill.currentAddress = {
     line1: customer.currentAddress.line1,
     line2: customer.currentAddress.line2,
     city: customer.currentAddress.city,
     postcode: customer.currentAddress.postcode,
    };
   }

   const additionalPayload = {
    journeyVariant: JOURNEY_VARIANTS.DUAL_REP,
    caseIds: customer.caseIds,
    agreementNumbers: customer.agreementNumbers,
    lenderEmail: customer.lenderEmail,
    lenderName: customer.lenderName,
    formattedAddress: customer.address,
   };
   if (fingerprint) {
    additionalPayload.journeySubmissionFingerprint = fingerprint;
   }

   const { response } = await submitMotJourney({
    uniqueID: effectiveUniqueID,
    clientid: customer.clientId || undefined,
    brand,
    datastoreWebFormId: DATASTORE_WEB_FORM_ID,
    kountPrefill,
    ipData,
    termsAccepted: false,
    additionalPayload,
   });

   if (response && response.status < 400 && response.data?.dataid) {
    if (!ipData?.dataid && response.data.dataid) {
     await saveSession(effectiveUniqueID, {
      dataid: response.data.dataid,
      ip: ipData?.ip,
     });
    }

    subscribeKlaviyo(
     customer.email,
     window.location.href,
     customer.mobileNumber,
     {
      firstName: customer.firstName,
      lastName: customer.lastName,
     },
    );
    trackKlaviyo("Submitted", {
     url: window.location.href,
     page_name: "SMS Lander",
     email: customer.email,
    });
   }
  } catch (_) {
   // Background failure — lander unchanged; refresh can retry
  }
 };

 // --- Render error page for invalid links ---
 if (!hasLenderEmail) {
  return (
   <ErrorPage
    brand={brand}
    message="The lender email address is missing from this personalised link."
   />
  );
 }

 if (!hasNames || !hasIdentifier) {
  return (
   <ErrorPage
    brand={brand}
    message="Some required customer details are missing from this personalised link."
   />
  );
 }


 const handleReplyToLender = () => {
  trackCustomEvent("sms_lander_reply_to_lender", {
   caseCount: customer.caseIds.length,
  });
 };

 return (
  <main className={`sms-lander sms-lander--${brand.id.toLowerCase()}`}>
   <div className="sms-lander__shell">
    <header className="sms-header">
     <div className="sms-header__logos">
      <img src={brand.logo} alt={brand.displayName} />
      {details.supportingLogo && (
       <img src={details.supportingLogo} alt="My Law Matters" />
      )}
     </div>
    </header>

    {/* Hello + body */}
    <section className="sms-message">
     <div className="sms-message__hello">
      <img
       className="sms-message__icon"
       src={icons.person}
       alt="Person Icon"
       width="28"
       height="28"
      />
      <h3>Hi {customer.firstName || ""},</h3>
     </div>
     <p className="sms-message__body">
      We've been notified by <strong>{customer.lenderName}</strong> that they
      currently have more than one firm recorded as acting on your behalf in
      relation to your motor finance commission claim. Don't worry, this is
      quick to resolve.
     </p>
     <p className="sms-message__body">
      As confirmed during sign-up, <strong>My Law Matters (MLM)</strong> is
      instructed to act on your behalf and is progressing your claim. However,{" "}
      <strong>{customer.lenderName}</strong> now needs you to confirm this
      directly by email.
     </p>
     <p className="sms-message__body">
      Thank you for your continued trust. We're committed to achieving the
      best possible outcome for you.
     </p>
    </section>

    {/* Next Steps heading */}
    <section className="sms-next-steps">
     <img
      className="sms-next-steps__icon"
      src={icons.nextSteps}
      alt="Next Steps Icon"
      width="28"
      height="28"
     />
     <h3 className="sms-next-steps__title">Next Steps:</h3>
    </section>

    {/* Action required box */}
    <section className="sms-action" aria-labelledby="sms-action-title">
     <h2 id="sms-action-title" className="sms-action__title">
      <img
       className="sms-action__icon"
       src={icons.action}
       alt="Action required icon"
       width="23"
       height="23"
      />
      <span>Action required</span>
     </h2>
     <div className="sms-action__row">
      <p className="sms-action__copy">
       Simply click the <strong>&lsquo;Reply to Lender&rsquo;</strong>{" "}
       button to automatically generate an email to send to{" "}
       {customer.lenderName}.
      </p>
      <div className="sms-action__cta action-button">
       <button
        type="button"
        className="btn-w color3 next-button"
        onClick={() => {
         handleReplyToLender();
         handleSubmit();
         window.location.href = mailto;
        }}
       >
        Reply to Lender
       </button>
      </div>
     </div>
    </section>

    {/* OR box */}
    <section className="sms-or-box" aria-label="Alternative instructions">
     <p className="sms-or-box__label">OR</p>

     <ol className="sms-steps">
      <li>
       Start a new email to{" "}
       <a href={`mailto:${customer.lenderEmail}`}>
        {customer.lenderEmail}
       </a>{" "}
       or open an email you have received from{" "}
       <strong>{customer.lenderName}</strong> and click
       &lsquo;Reply&rsquo;.
      </li>
      <li>
       Ensure you cc.{" "}
       <strong>{details.ccEmail}</strong> so that we can follow up
       with your lender to check your case is progressing.
      </li>
      <li>Copy and paste the message below.</li>
      <li>
       Insert your DOB and Address, and send to{" "}
       <strong>{customer.lenderName}</strong>.
      </li>
     </ol>

     <div className="sms-email" aria-label="Email to copy and paste">
      <p>Dear Sir/Madam,</p>
      <p>
       I confirm that I have appointed My Law Matters, SRA number 800557,
       to act on my behalf in relation to any and all of my motor finance
       commission complaints.
      </p>
      <p>
       I confirm that I have withdrawn my authority and representation from
       any previous professional representative in relation to these
       complaints and that they are no longer authorised to act on my behalf.
      </p>
      <p>
       I also confirm that I do not authorise or require any compensation,
       redress or other payment arising from my complaints to be paid to any
       previous professional representative.
      </p>
      <p>
       Please remove any previous professional representative from your
       records, record My Law Matters as my sole appointed representative,
       and ensure that all future correspondence regarding my complaints is
       directed to My Law Matters.
      </p>
      <div>
       <p>Kind regards,</p>
       <p>
        {customer.firstName} {customer.lastName}
       </p>
       <p>Reference: {reference}</p>
       <p>
        Agreement Number(s) - last 4 digits: {agreementShort}
       </p>
       <p>Date of Birth:</p>
       <p>Address:</p>
      </div>
     </div>
    </section>

   </div>
  </main>
 );
}
